import React, { type ReactNode, type ReactElement } from 'react';
import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';
import type { AdditionalEventAttributes } from '@atlassian/jira-common-experience-tracking-analytics';
import {
	ViewExperienceTracker,
	ViewExperienceTrackingProvider,
} from '@atlassian/jira-common-experience-tracking-viewing';
import type { TrackerCallbacks } from '@atlassian/jira-common-experience-tracking-viewing/src/view/common/types';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types';
import { getErrorAttributes } from '@atlassian/jira-software-logging';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	EXPERIENCE_TRACKER_LOCATION_BACKLOG,
	ANALYTICS_SOURCE_BACKLOG,
} from '../../common/constants/analytics';
import { useIsCMPBacklog } from '../../state/state-hooks/capabilities';

export type SLATrackerCallbacks = {
	onSuccess: () => void;
	onError: (location: string, error: Error) => void;
};

export type Props = {
	/**
	 * The identifier that generates the experienceId used in ViewExperienceTrackingProvider
	 * Changes to this id reset the experience tracking
	 */
	experienceTargetId?: string;
	/**
	 * The name of the tracking target that generates experienceId, experience and location used in ViewExperienceTrackingProvider and ViewExperienceTracker
	 * Not defining it means tracking provider already exists in parent levels
	 */
	experienceTarget?: string;
	/**
	 * The analytics source used in ViewExperienceTrackingProvider
	 * Please do not mix it with analytics source used in common analytics events unless they are set to be the same on purpose
	 */
	experienceSource?: string;
	/**
	 * Additional attributes that are added/overridden in the events fired by onExperienceSuccess and/or onExperienceFailure callbacks
	 */
	additionalAttributes?: AdditionalEventAttributes;
	children: ReactNode | ((slaTrackerCallbacks: SLATrackerCallbacks) => ReactElement);
};

const SLAExperienceTracker = ({
	experienceTarget = '',
	experienceTargetId = '',
	experienceSource = ANALYTICS_SOURCE_BACKLOG,
	additionalAttributes = {},
	children,
}: Props) => {
	const {
		appEditions: { software },
	} = useTenantContext();

	const isCompanyManaged = useIsCMPBacklog();
	const experienceTargetKey: string = camelCase(experienceTarget);
	const experienceId: string = experienceTargetId
		? `${experienceTargetKey}-${experienceTargetId}`
		: experienceTargetKey;
	const experience = `view${experienceTargetKey
		.charAt(0)
		.toUpperCase()}${experienceTargetKey.slice(1)}`;
	const experienceLocation: string = experienceTarget
		? `${EXPERIENCE_TRACKER_LOCATION_BACKLOG}-${kebabCase(experienceTarget)}`
		: EXPERIENCE_TRACKER_LOCATION_BACKLOG;

	const onSuccess =
		({ onExperienceSuccess }: TrackerCallbacks) =>
		() => {
			onExperienceSuccess();
		};

	const onError =
		({ onExperienceFailure }: TrackerCallbacks) =>
		(location: string, error: Error) => {
			onExperienceFailure(undefined, getErrorAttributes(error));
		};

	const viewExperienceTracker = (
		<ViewExperienceTracker location={experienceLocation}>
			{(trackerCallbacks: TrackerCallbacks) =>
				typeof children === 'function' ? (
					children({
						onSuccess: onSuccess(trackerCallbacks),
						onError: onError(trackerCallbacks),
					})
				) : (
					<>
						{children}
						<OpTrigger op={trackerCallbacks.onExperienceSuccess} />
					</>
				)
			}
		</ViewExperienceTracker>
	);

	return experienceTarget ? (
		<ViewExperienceTrackingProvider
			experienceId={experienceId}
			experience={experience}
			analyticsSource={experienceSource}
			edition={software}
			application={JIRA_SOFTWARE}
			additionalAttributes={{
				isUIF: true,
				isClassic: isCompanyManaged,
				...additionalAttributes,
			}}
		>
			{viewExperienceTracker}
		</ViewExperienceTrackingProvider>
	) : (
		viewExperienceTracker
	);
};

export default SLAExperienceTracker;
